import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'

interface LegalPageProps {
    title: string
    intro?: ReactNode
    updated: string
    children: ReactNode
}

interface LegalSectionProps {
    title: string
    children: ReactNode
    className?: string
}

interface PlaceholderProps {
    children: ReactNode
}

// Shared frame for Impressum, Datenschutz and Barrierefreiheit.
export const LegalPage: FC<LegalPageProps> = ({ title, intro, updated, children }): ReactNode => {
    return (
        <>
            <section className='surface-dark bg-petrol-900 text-kalk-50'>
                <Container className='pt-36 pb-14 md:pt-44 md:pb-20'>
                    <Eyebrow tone='onDark'>
                        Rechtliches
                    </Eyebrow>

                    <h1 className='mt-6 font-heading stretch-wide text-[clamp(2.2rem,5vw,3.75rem)] leading-[1.02] font-semibold tracking-[-0.03em] hyphens-auto'>
                        {title}
                    </h1>

                    {intro && (
                        <p className='mt-6 max-w-2xl text-lg leading-relaxed text-petrol-200'>
                            {intro}
                        </p>
                    )}
                </Container>
            </section>

            <Container className='py-16 md:py-24'>
                <div className='mx-auto flex max-w-3xl flex-col gap-12'>
                    {children}

                    <p className='border-t border-border pt-6 text-sm text-muted-foreground'>
                        Stand: {updated}
                    </p>
                </div>
            </Container>
        </>
    )
}

export const LegalSection: FC<LegalSectionProps> = ({ title, children, className }): ReactNode => {
    return (
        <section className={cn('flex flex-col gap-4', className)}>
            <h2 className='font-heading stretch-semiwide text-xl font-semibold tracking-[-0.01em] md:text-2xl'>
                {title}
            </h2>

            <div className='flex flex-col gap-4 text-base leading-relaxed text-foreground/85 [&_a]:font-medium [&_a]:text-tinte [&_a]:underline [&_a]:underline-offset-3 [&_a:hover]:text-signal-600 [&_li]:pl-1 [&_ul]:flex [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-2 [&_ul]:pl-5'>
                {children}
            </div>
        </section>
    )
}

// Marks information the company still has to supply. Never replace it with invented details.
export const Placeholder: FC<PlaceholderProps> = ({ children }): ReactNode => {
    return (
        <mark
            data-platzhalter=''
            className='rounded-md border border-dashed border-signal-600/60 bg-signal-600/8 px-1.5 py-0.5 font-mono text-[0.85em] text-signal-700'
        >
            [{children}]
        </mark>
    )
}
